import axios from 'axios'
import Axhelper from '@/lib/Axhelper'
import { normalizeRozliczono } from '@/pages/centrumSzkolen/Projekty/details/projectTrainerHelpers'
import { normalizePozycje } from '@/pages/centrumSzkolen/Projekty/common/normalizePozycje'

const getRelationId = value => {
	if (!value) return ''
	if (typeof value === 'object') {
		return `${value?._id || value?.id || ''}`.trim()
	}
	return `${value}`.trim()
}

const parseCertificates = value => {
	if (Array.isArray(value)) {
		return value
			.map(item => ({
				certificateId: `${item?.certificateId ?? item?.certificationId ?? ''}`.trim(),
				certificateName: `${item?.certificateName ?? item?.name ?? item?.nazwa ?? ''}`.trim(),
			}))
			.filter(item => item.certificateId)
	}

	if (typeof value === 'string' && value.trim()) {
		try {
			return parseCertificates(JSON.parse(value))
		} catch {
			return []
		}
	}

	return []
}

const normalizeProjectFiles = files => (
	Array.isArray(files) ? files : []
).map(item => ({
	fileUuid: `${item?.fileUuid || item?._id || ''}`.trim(),
	filePath: `${item?.filePath || ''}`.trim(),
	originalName: `${item?.originalName || item?.fileName || item?.name || ''}`.trim(),
})).filter(item => item.fileUuid)

const extractPlacowkaFields = entity => {
	const embedded = entity?.placowkaId && typeof entity.placowkaId === 'object'
		? entity.placowkaId
		: null

	return {
		placowkaId: getRelationId(entity?.placowkaId) || null,
		placowkaName: `${entity?.placowkaName || embedded?.nazwa || ''}`.trim(),
		placowkaAddress: `${entity?.placowkaAddress || embedded?.adres || ''}`.trim(),
		id_miasto: entity?.id_miasto || embedded?.id_miasto || null,
		id_gmina: entity?.id_gmina || embedded?.id_gmina || null,
		id_powiat: entity?.id_powiat || embedded?.id_powiat || null,
		id_wojewodztwo: entity?.id_wojewodztwo || embedded?.id_wojewodztwo || null,
		id_typ: entity?.id_typ || embedded?.id_typ || null,
	}
}

const mapApiStepToUi = step => {
	if (!step || typeof step !== 'object') return step

	const placowkaFields = extractPlacowkaFields(step)
	const certificates = parseCertificates(step.certificates)
	const requiredCertificationIds = Array.isArray(step.requiredCertificationIds) && step.requiredCertificationIds.length
		? step.requiredCertificationIds
		: certificates.map(item => item.certificateId).filter(Boolean)
	const adres = `${step.adres ?? step.ulica ?? ''}`.trim()
	const miasto = `${step.miasto ?? ''}`.trim()
	const kod_pocztowy = `${step.kod_pocztowy || ''}`.trim()
	const pozycje = normalizePozycje(step.pozycje, {
		keepEmpty: true,
		fallbackCertificates: certificates,
	})
	const trainerNames = pozycje.map(item => item.trainerName).filter(Boolean)
	const locationParts = [adres, miasto, kod_pocztowy].filter(Boolean)

	return {
		...step,
		...placowkaFields,
		_id: step._id || step.id || null,
		nazwa: step.nazwa || '',
		certificates,
		requiredCertificationIds,
		pozycje,
		trainerNames,
		trainerName: trainerNames.join(', '),
		selectedTrainerIds: pozycje.map(item => item.trainerId).filter(Boolean),
		data_rozpoczecia: step.data_rozpoczecia || '',
		data_zakonczenia: step.data_zakonczenia || step.data_rozpoczecia || '',
		startTime: step.startTime || step.godzina_rozpoczecia || '',
		endTime: step.endTime || step.godzina_zakonczenia || '',
		godzina_rozpoczecia: step.godzina_rozpoczecia || step.startTime || '',
		godzina_zakonczenia: step.godzina_zakonczenia || step.endTime || '',
		ulica: adres,
		adres,
		miasto,
		kod_pocztowy,
		location: step.location || locationParts.join(', '),
		notatki: step.notatki || '',
		osoba_kontaktowa: {
			nazwisko: `${step.osoba_kontaktowa?.nazwisko ?? ''}`.trim(),
			funkcja: `${step.osoba_kontaktowa?.funkcja ?? ''}`.trim(),
			telefon: `${step.osoba_kontaktowa?.telefon ?? ''}`.trim(),
		},
		status: step.status || 'new',
	}
}

const mapApiProjectToUi = project => {
	if (!project || typeof project !== 'object') return project

	const steps = (Array.isArray(project.steps) ? project.steps : []).map(mapApiStepToUi)
	const allTrainerNames = steps.flatMap(step => step.trainerNames || []).filter(Boolean)
	const projectFields = { ...project }
	delete projectFields.data_rozpoczecia

	return {
		...projectFields,
		nazwa: project.nazwa || '',
		status: project.status || 'new',
		opis: project.opis || '',
		uwagi: project.uwagi || '',
		remarks: project.uwagi || project.remarks || '',
		steps,
		stepsCount: steps.length,
		trainerNames: [...new Set(allTrainerNames)],
		projectFiles: normalizeProjectFiles(project.projectfiles || project.projectFiles),
	}
}

const serializeStep = step => {
	const placowkaId = getRelationId(step?.placowkaId) || null
	const certificates = parseCertificates(step?.certificates)
	const pozycje = normalizePozycje(step?.pozycje)

	const payload = {
		nazwa: step?.nazwa ?? '',
		placowkaId,
		placowkaName: step?.placowkaName ?? '',
		certificates,
		status: step?.status ?? 'new',
		data_rozpoczecia: step?.data_rozpoczecia ?? '',
		data_zakonczenia: step?.data_zakonczenia ?? step?.data_rozpoczecia ?? '',
		godzina_rozpoczecia: step?.startTime ?? step?.godzina_rozpoczecia ?? '',
		godzina_zakonczenia: step?.endTime ?? step?.godzina_zakonczenia ?? '',
		adres: `${step?.ulica ?? step?.adres ?? ''}`.trim(),
		miasto: step?.miasto ?? '',
		kod_pocztowy: step?.kod_pocztowy ?? '',
		g_placeid: step?.g_placeid ?? '',
		notatki: step?.notatki ?? '',
		osoba_kontaktowa: {
			nazwisko: step?.osoba_kontaktowa?.nazwisko ?? '',
			funkcja: step?.osoba_kontaktowa?.funkcja ?? '',
			telefon: step?.osoba_kontaktowa?.telefon ?? '',
		},
		pozycje: pozycje.map(item => ({
			...(item._id ? { _id: item._id } : {}),
			value: item.value || '',
			start_time: item.start_time || '',
			end_time: item.end_time || '',
			certificates: parseCertificates(item.certificates),
			trainerId: item.trainerId || '',
			trainerName: item.trainerName || '',
			miasto: item.miasto ?? '',
			odleglosc_km: item.odleglosc_km ?? '',
			odleglosc_z_domu_km: item.odleglosc_z_domu_km ?? '',
			odleglosc_z_poprzedniej_placowki_km: item.odleglosc_z_poprzedniej_placowki_km ?? '',
			odleglosc_drogi_google_km: item.odleglosc_drogi_google_km ?? '',
			typ_stawki_godzinowej: item.typ_stawki_godzinowej || 'U',
			stawka_godzinowa: item.stawka_godzinowa ?? '',
			stawka_za_kilometr: item.stawka_za_kilometr ?? 1,
			ilosc_godzin: item.ilosc_godzin ?? '',
			do_wyplaty: item.do_wyplaty ?? '',
			rozliczono: normalizeRozliczono(item.rozliczono),
			inne_koszty: (Array.isArray(item.inne_koszty) ? item.inne_koszty : []).map(cost => ({
				tytul: `${cost?.tytul ?? ''}`.trim(),
				kwota_brutto: cost?.kwota_brutto ?? '',
				notatka: `${cost?.notatka ?? ''}`.trim(),
			})),
			uwagi: `${item.uwagi ?? ''}`.trim(),
		})),
	}

	if (step?._id) payload._id = step._id
	if (step?.g_lat !== '' && step?.g_lat != null) payload.g_lat = Number(step.g_lat)
	if (step?.g_long !== '' && step?.g_long != null) payload.g_long = Number(step.g_long)

	return payload
}

class ProjectsService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG?.API_URL || ''
		this.endpoints = {
			projects: '/api/projekty/filters',
			projectsDetails: '/api/projekty/details',
			projectsCreate: '/api/projekty',
			projectsUpdate: '/api/projekty',
			projectsDelete: '/api/projekty',
			uploadProjectFile: '/api/projekty/uploadprojectfile',
			downloadProjectFile: '/api/projekty/downloadprojectfile',
			deleteProjectFile: '/api/projekty/deleteprojectfile',
			getContentOptions: '/api/projekty/getcontentoptions',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 25,
				sortColumn = 'created',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.projects}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: [], error: response.error, totalCount: 0, hasMore: false }
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('ProjectsService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch projects',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(projectId) {
		try {
			if (!projectId) {
				return { data: null, error: 'Invalid project ID provided' }
			}

			const url = `${this.endpoints.projectsDetails}/${encodeURIComponent(projectId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: null, error: response.error }
			}

			return {
				data: mapApiProjectToUi(response.data),
				error: null,
			}
		} catch (error) {
			console.error('ProjectsService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch project',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			const trimmed = `${searchTerm || ''}`.trim()
			if (!trimmed) {
				return { data: [], error: 'Invalid search term provided', totalCount: 0, hasMore: false }
			}

			const {
				limit = 25,
				sortColumn = 'created',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = this._buildParams({ limit, sortColumn, sortDirection, filters })
			const url = `${this.endpoints.projects}?searchText=${encodeURIComponent(trimmed)}&${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: [], error: response.error, totalCount: 0, hasMore: false }
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('ProjectsService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search projects',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getContentOptions() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.getContentOptions)

			if (response.error) {
				return { data: [], error: response.error }
			}

			const items = Array.isArray(response.data)
				? response.data
				: response.data?.content || response.data?.options || []

			return { data: this._normalizeContentOptions(items), error: null }
		} catch (error) {
			console.error('ProjectsService.getContentOptions error:', error)
			return { data: [], error: error.message || 'Failed to fetch content options' }
		}
	}

	async create(projectData) {
		try {
			const nazwa = `${projectData?.nazwa ?? ''}`.trim()
			if (!nazwa) {
				return { success: false, error: 'Project name is required', data: null }
			}

			const params = new URLSearchParams()
			params.append('nazwa', nazwa)
			if (projectData.status) params.append('status', projectData.status)

			const response = await Axhelper.sendPostAsync(this.endpoints.projectsCreate, params)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			return {
				success: true,
				error: null,
				data: mapApiProjectToUi(response.data),
			}
		} catch (error) {
			console.error('ProjectsService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create project',
				data: null,
			}
		}
	}

	async update(projectId, projectData) {
		try {
			if (!projectId) {
				return { success: false, error: 'Invalid project ID provided', data: null }
			}

			const nazwa = `${projectData?.nazwa ?? ''}`.trim()
			if (!nazwa) {
				return { success: false, error: 'Project name is required', data: null }
			}

			const payload = {
				nazwa,
				status: projectData.status || 'new',
				opis: projectData.opis ?? '',
				uwagi: projectData.uwagi ?? projectData.remarks ?? '',
			}

			if (Object.prototype.hasOwnProperty.call(projectData, 'steps')) {
				payload.steps = (Array.isArray(projectData.steps) ? projectData.steps : []).map(serializeStep)
			}

			const url = `${this.endpoints.projectsUpdate}/${encodeURIComponent(projectId)}`
			const response = await Axhelper.sendPutAsync(url, payload)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			return {
				success: true,
				error: null,
				data: mapApiProjectToUi(response.data),
			}
		} catch (error) {
			console.error('ProjectsService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update project',
				data: null,
			}
		}
	}

	async delete(projectId) {
		try {
			if (!projectId) {
				return { success: false, error: 'Invalid project ID provided', message: null }
			}

			const url = `${this.endpoints.projectsDelete}/${encodeURIComponent(projectId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return { success: false, error: response.error, message: null }
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Project deleted successfully.',
			}
		} catch (error) {
			console.error('ProjectsService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete project',
				message: null,
			}
		}
	}

	async uploadProjectFile(projectId, file, onUploadProgress) {
		try {
			if (!projectId) {
				return { success: false, error: 'Invalid project ID provided', data: null }
			}
			if (!file) {
				return { success: false, error: 'No file provided', data: null }
			}

			const formData = new FormData()
			formData.append('file', file)
			const url = `${this.endpoints.uploadProjectFile}/${encodeURIComponent(projectId)}`
			const response = await Axhelper.sendUploadAsync(url, formData, onUploadProgress)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			return { success: true, error: null, data: mapApiProjectToUi(response.data) }
		} catch (error) {
			console.error('ProjectsService.uploadProjectFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to upload project file',
				data: null,
			}
		}
	}

	async downloadProjectFile(projectId, fileUuid) {
		try {
			if (!projectId || !fileUuid) {
				return { success: false, error: 'Invalid project file reference', blob: null, filename: null }
			}

			const url = `${this.endpoints.downloadProjectFile}/${encodeURIComponent(projectId)}/${encodeURIComponent(fileUuid)}`
			const response = await this._downloadBlob(url)

			if (response.error) {
				return { success: false, error: response.error, blob: null, filename: null }
			}

			const filename = this._extractDownloadFilename(response.headers, fileUuid, projectId)
			return { success: true, error: null, blob: response.blob, filename }
		} catch (error) {
			console.error('ProjectsService.downloadProjectFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to download project file',
				blob: null,
				filename: null,
			}
		}
	}

	async deleteProjectFile(projectId, fileUuid) {
		try {
			if (!projectId || !fileUuid) {
				return { success: false, error: 'Invalid project file reference', data: null }
			}

			const url = `${this.endpoints.deleteProjectFile}/${encodeURIComponent(projectId)}/${encodeURIComponent(fileUuid)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			return { success: true, error: null, data: mapApiProjectToUi(response.data) }
		} catch (error) {
			console.error('ProjectsService.deleteProjectFile error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete project file',
				data: null,
			}
		}
	}

	_buildParams({ limit, sortColumn, sortDirection, filters }) {
		const params = new URLSearchParams({
			sortcol: sortColumn,
			sortdir: sortDirection,
			limit: String(limit),
		})

		Object.entries(filters || {}).forEach(([key, value]) => {
			if (key === 'searchText' || value === undefined || value === null || value === '') return
			params.append(key, String(value))
		})

		return params
	}

	_buildListResponse(response) {
		const rows = (Array.isArray(response.data) ? response.data : []).map(mapApiProjectToUi)
		const totalCount = response.headers?.['x-total-count']
			? parseInt(response.headers['x-total-count'], 10)
			: rows.length

		return {
			data: rows,
			error: null,
			totalCount,
			hasMore: rows.length < totalCount,
			headers: response.headers,
		}
	}

	_normalizeContentOptions(items = []) {
		return (Array.isArray(items) ? items : []).map(item => {
			if (typeof item === 'string') {
				const value = item.trim()
				return value ? { value, label: value } : null
			}

			const value = `${item?.nazwa ?? item?.name ?? item?.value ?? ''}`.trim()
			return value ? { value, label: value } : null
		}).filter(Boolean)
	}

	async _downloadBlob(url) {
		try {
			const token = localStorage.getItem('token')
			const response = await axios.get(url, {
				responseType: 'blob',
				withCredentials: true,
				headers: { token },
			})

			return { error: null, blob: response.data, headers: response.headers }
		} catch (error) {
			const errorMessage = error.response?.data || error.message || 'Download failed'
			return { error: errorMessage, blob: null, headers: null }
		}
	}

	_extractDownloadFilename(headers, fileUuid, projectId) {
		const defaultFilename = `project-file-${projectId}-${fileUuid}`

		if (!headers) return defaultFilename

		const disposition = headers['content-disposition'] || ''
		const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
		if (utf8Match?.[1]) {
			return decodeURIComponent(utf8Match[1])
		}

		const regularMatch = disposition.match(/filename="?([^";]+)"?/i)
		if (regularMatch?.[1]) {
			return regularMatch[1]
		}

		return defaultFilename
	}
}

export default new ProjectsService()
